/**
 * API Error Types
 *
 * This module contains types for error handling, classification, and reporting.
 * It includes error classification types for categorizing API errors as well as
 * structures for error reporting to monitoring services.
 *
 * @module types/api/error.types
 */

import type { RequestConfig } from './request.types';

// =============================================================================
// ERROR CLASSIFICATION TYPES
// =============================================================================

/**
 * Enumeration of error types for API error classification.
 *
 * Error types help categorize the nature of an error to determine
 * appropriate handling strategies (retry, auth refresh, user notification, etc.).
 *
 * @example
 * ```typescript
 * import { ErrorType } from '../../types/api';
 *
 * function handleError(error: Error, type: ErrorType) {
 *   switch (type) {
 *     case ErrorType.NETWORK:
 *       // Handle network connectivity issues
 *       break;
 *     case ErrorType.AUTHENTICATION:
 *       // Redirect to login
 *       break;
 *     case ErrorType.RATE_LIMIT:
 *       // Back off and retry later
 *       break;
 *   }
 * }
 * ```
 */
export enum ErrorType {
  /** Network connectivity error (no response received) */
  NETWORK = 'network',
  /** HTTP error (response received with error status) */
  HTTP = 'http',
  /** Request timeout error */
  TIMEOUT = 'timeout',
  /** Request was cancelled */
  CANCELLED = 'cancelled',
  /** Authentication error (401 - invalid/expired credentials) */
  AUTHENTICATION = 'authentication',
  /** Authorization error (403 - insufficient permissions) */
  AUTHORIZATION = 'authorization',
  /** Validation error (400 - invalid request data) */
  VALIDATION = 'validation',
  /** Rate limit exceeded (429 - too many requests) */
  RATE_LIMIT = 'rate_limit',
  /** Server error (5xx status codes) */
  SERVER_ERROR = 'server_error',
  /** Client error (4xx status codes not covered above) */
  CLIENT_ERROR = 'client_error',
  /** Unknown or unclassified error */
  UNKNOWN = 'unknown',
}

/**
 * Enumeration of error categories for determining handling strategy.
 *
 * Error categories group errors by how they should be handled,
 * particularly regarding retry behavior and user interaction.
 *
 * @example
 * ```typescript
 * import { ErrorCategory } from '../../types/api';
 *
 * if (classification.category === ErrorCategory.RETRYABLE) {
 *   // Automatically retry the request
 * } else if (classification.category === ErrorCategory.AUTH_REQUIRED) {
 *   // Refresh auth tokens before retrying
 * }
 * ```
 */
export enum ErrorCategory {
  /** Error can be retried automatically (transient failures) */
  RETRYABLE = 'retryable',
  /** Error should not be retried (permanent failures) */
  NON_RETRYABLE = 'non_retryable',
  /** Authentication refresh required before retry */
  AUTH_REQUIRED = 'auth_required',
  /** Circuit breaker should be triggered */
  CIRCUIT_BREAKER = 'circuit_breaker',
  /** User action is required to resolve the error */
  USER_ACTION_REQUIRED = 'user_action_required',
}

/**
 * Enumeration of error priority levels for alerting and logging.
 *
 * Priority levels help determine the urgency of error handling
 * and appropriate notification channels.
 *
 * @example
 * ```typescript
 * import { ErrorPriority } from '../../types/api';
 *
 * if (classification.priority === ErrorPriority.CRITICAL) {
 *   // Trigger immediate alert
 *   alertOps(errorReport);
 * }
 * ```
 */
export enum ErrorPriority {
  /** Low priority - informational, no immediate action needed */
  LOW = 'low',
  /** Medium priority - should be addressed but not urgent */
  MEDIUM = 'medium',
  /** High priority - requires prompt attention */
  HIGH = 'high',
  /** Critical priority - requires immediate attention */
  CRITICAL = 'critical',
}

/**
 * Complete classification of an API error.
 *
 * This interface provides a comprehensive classification of an error
 * including its type, category, retry eligibility, and priority level.
 * Used by the error classifier to determine appropriate handling.
 *
 * @example
 * ```typescript
 * import type { ErrorClassification } from '../../types/api';
 * import { ErrorType, ErrorCategory, ErrorPriority } from '../../types/api';
 *
 * const classification: ErrorClassification = {
 *   type: ErrorType.SERVER_ERROR,
 *   category: ErrorCategory.RETRYABLE,
 *   shouldRetry: true,
 *   shouldRefreshAuth: false,
 *   shouldTransform: true,
 *   retryDelay: 1000,
 *   circuitBreakerTrip: false,
 *   priority: ErrorPriority.HIGH,
 * };
 *
 * if (classification.shouldRetry && classification.retryDelay) {
 *   await delay(classification.retryDelay);
 *   // Retry the request
 * }
 * ```
 */
export interface ErrorClassification {
  /** The specific type of error */
  type: ErrorType;

  /** The handling category for the error */
  category: ErrorCategory;

  /** Whether the request should be automatically retried */
  shouldRetry: boolean;

  /** Whether authentication tokens should be refreshed before retry */
  shouldRefreshAuth: boolean;

  /** Whether the error should be transformed for user display */
  shouldTransform: boolean;

  /** Suggested delay before retry (in milliseconds) */
  retryDelay?: number;

  /** Whether this error should trip the circuit breaker */
  circuitBreakerTrip?: boolean;

  /** Priority level for logging and alerting */
  priority: ErrorPriority;
}

/**
 * Interface for the ErrorClassifier.
 *
 * Defines the contract for the central orchestrator that classifies errors,
 * including if an error should be retried, how long to wait before retrying,
 * and if an error should trigger an auth refresh.
 *
 * @example
 * ```typescript
 * const classifier: IErrorClassifier = createErrorClassifier(retryConfig);
 *
 * // Classify an error
 * const classification = classifier.classifyError(error, config);
 *
 * // Check if an error should retry
 * const shouldRetry = classifier.shouldRetry(classification);
 *
 * // Check if an error should trigger an auth refresh
 * const shouldRefreshAuth = classifier.shouldRefreshAuth(classification);
 *
 * // Calculate the delay before retrying
 * const retryDelay = classifier.calculateRetryDelay(classification);
 *
 * // Check if an error should trigger a circuit breaker trip
 * const shouldTripCircuitBreaker = classifier.shouldTripCircuitBreaker(classification);
 * ```
 */
export interface IErrorClassifier {
  /**
   * Classifies an error based on its type and context.
   *
   * @param error - The error object to classify.
   * @param config - The request configuration associated with the error.
   * @returns The error classification.
   */
  classifyError(error: Error, config: RequestConfig): ErrorClassification;

  /**
   * Determines whether an error should be retried.
   *
   * @param classification - The error classification.
   * @returns True if the error should be retried, false otherwise.
   */
  shouldRetry(classification: ErrorClassification): boolean;

  /**
   * Determines whether an error should trigger authentication refresh.
   *
   * @param classification - The error classification.
   * @returns True if the error should trigger authentication refresh, false otherwise.
   */
  shouldRefreshAuth(classification: ErrorClassification): boolean;

  /**
   * Determines whether an error should trigger data transformation.
   *
   * @param classification - The error classification.
   * @returns True if the error should trigger data transformation, false otherwise.
   */
  shouldTransform(classification: ErrorClassification): boolean;

  /**
   * Calculates the delay before retrying an operation.
   *
   * @param attempt - The current retry attempt number.
   * @param classification - The error classification.
   * @returns The delay in milliseconds before retrying.
   */
  calculateRetryDelay(
    attempt: number,
    classification: ErrorClassification
  ): number;

  /**
   * Determines whether an error should trigger a circuit breaker.
   *
   * @param classification - The error classification.
   * @returns True if the error should trigger a circuit breaker, false otherwise.
   */
  isCircuitBreakerCandidate(classification: ErrorClassification): boolean;
}

// =============================================================================
// SERVICE ERROR REPORTING TYPES
// =============================================================================

/**
 * Enhanced error report interface for monitoring services.
 *
 * This interface provides a comprehensive structure for reporting errors to
 * external monitoring and analytics services. It includes error details,
 * environment context, and metadata for debugging.
 *
 * @example
 * ```typescript
 * const errorReport: ServiceErrorReport = {
 *   errorId: 'err_1234567890_abc123',
 *   correlationId: 'corr_1234567890_xyz789',
 *   timestamp: '2024-01-15T10:30:00.000Z',
 *   environment: 'production',
 *   service: 'personal-trainer-app-frontend',
 *   version: '1.2.0',
 *   error: {
 *     code: 'NETWORK_ERROR',
 *     message: 'Failed to fetch data',
 *     category: 'network',
 *     severity: 'medium',
 *     status: undefined,
 *     url: '/api/workouts',
 *     method: 'GET',
 *   },
 *   context: {
 *     userAgent: 'Mozilla/5.0...',
 *     pathname: '/dashboard',
 *   },
 *   metadata: {},
 * };
 * ```
 */
export interface ServiceErrorReport {
  /**
   * Unique identifier for this error report.
   * Format: `err_<timestamp>_<random>`
   */
  errorId: string;

  /**
   * Correlation ID for tracking the error across services.
   * Can be used to link related errors and requests.
   */
  correlationId: string;

  /**
   * ISO 8601 timestamp when the error occurred.
   */
  timestamp: string;

  /**
   * Environment where the error occurred (e.g., 'development', 'production').
   */
  environment: string;

  /**
   * Name of the service that generated the error.
   */
  service: string;

  /**
   * Application version, if available.
   */
  version?: string;

  /**
   * Error details.
   */
  error: {
    /** Error code identifying the type of error */
    code: string;
    /** Human-readable error message */
    message: string;
    /** Error category for classification */
    category: string;
    /** Error severity level */
    severity: string;
    /** HTTP status code, if applicable */
    status?: number;
    /** Request URL that caused the error */
    url?: string;
    /** HTTP method of the request */
    method?: string;
    /** Unique request identifier */
    requestId?: string;
    /** Additional error details */
    details?: Record<string, unknown>;
    /** Error stack trace */
    stack?: string;
  };

  /**
   * Browser and user context information.
   */
  context: {
    /** Browser user agent string */
    userAgent?: string;
    /** Session identifier */
    sessionId?: string;
    /** User identifier */
    userId?: string;
    /** Client IP address */
    ip?: string;
    /** HTTP referer header */
    referer?: string;
    /** Current page pathname */
    pathname?: string;
    /** URL search/query string */
    search?: string;
  };

  /**
   * Additional metadata for debugging and analytics.
   */
  metadata: Record<string, unknown>;
}
